import { biToast } from '@pulsepointinc/bi-components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient, SHORT_STALE_TIME } from '../../lib';
import type { ApiError } from '../../lib/api';
import {
  downloadNPIsListAPI,
  getNPIListsMetadataAPI,
  publishNPIsListAPI,
  removeNPIListAPI,
} from '../../lib/api/npiList/api';
import type {
  CreateNPIListRequest,
  CreateNPIListResponse,
  DownloadNPIsRequest,
  DownloadNPIsResponse,
  GetNpiListsMetadataRequest,
  GetNpiListsMetadataResponse,
  RemoveNPIListRequest,
  RemoveNPIListResponse,
} from '../../lib/api/npiList/dto';
import { API_TAGS } from '../../lib/constants';

interface NpiQueryOptions {
  enabled?: boolean;
  refetchOnMount?: boolean;
  refetchOnWindowFocus?: boolean;
}

export function useGetNPIListsMetadataQuery(params: GetNpiListsMetadataRequest, options?: NpiQueryOptions) {
  const queryKey = ['npiListsMetadata', params];
  return useQuery<GetNpiListsMetadataResponse, ApiError>({
    queryKey,
    queryFn: () => getNPIListsMetadataAPI(params),
    meta: {
      onError: () => {
        biToast.error('Failed to Load', 'Unable to load NPI lists. Please refresh the page.');
      },
    },
    staleTime: SHORT_STALE_TIME,
    ...options,
  });
}

export function useDownloadNPIsListMutation() {
  return useMutation<DownloadNPIsResponse, ApiError, DownloadNPIsRequest>({
    mutationFn: downloadNPIsListAPI,
    mutationKey: ['downloadNpiList'],
    onError: () => {
      biToast.error('Download Failed', 'Unable to download NPI list. Please try again.');
    },
  });
}

export function usePublishNPIsListMutation() {
  return useMutation<CreateNPIListResponse, ApiError, CreateNPIListRequest>({
    mutationFn: publishNPIsListAPI,
    mutationKey: ['publishNpiList'],
    onError: () => {
      biToast.error('NPI list publishing error. Try again.');
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [API_TAGS.Workspaces] });
    },
  });
}

export function useRemoveNPIListMutation() {
  return useMutation<RemoveNPIListResponse, ApiError, RemoveNPIListRequest>({
    mutationFn: removeNPIListAPI,
    mutationKey: ['removeNpiList'],
    onError: () => {
      biToast.error('NPI list unpublishing error. Try again.');
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [API_TAGS.Workspaces] });
    },
  });
}
